import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Camera, ShoppingBag, ExternalLink, BookHeart } from "lucide-react";
import { fetchBooks, fetchNews, fetchRecommendations, recoGenreLabel } from "../lib/api";
import { BookCard } from "../components/BookCard";
import { BookDetailDialog } from "../components/BookDetailDialog";
import { NewsDetailDialog } from "../components/NewsDetailDialog";
import { Marquee } from "../components/Marquee";
import { HeroStack } from "../components/HeroStack";
import { Collections } from "../components/Collections";
import { FeaturedBook } from "../components/FeaturedBook";
import { HomeStories } from "../components/HomeStories";

export default function Accueil() {
  const [featured, setFeatured] = useState([]);
  const [news, setNews] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    fetchBooks({ featured: true }).then(setFeatured).catch(() => setFeatured([]));
    fetchNews().then(setNews).catch(() => setNews([]));
    fetchRecommendations().then((r) => setRecommendations(r.slice(0, 3))).catch(() => setRecommendations([]));
  }, []);

  return (
    <div data-testid="page-accueil">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-6 md:px-12 lg:px-24 bg-[#FAF9F6]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.3em] uppercase text-[#9E4735] font-body mb-6 fade-in-up">
              Maison d'édition indépendante · Depuis 2018
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] leading-[1.05] tracking-tight font-light mb-8 fade-in-up" style={{ animationDelay: "100ms" }}>
              Histoires<br/>
              <em className="text-[#2C402E]">captivantes,</em><br/>
              <span className="text-[#9E4735]">plumes</span> singulières.
            </h1>
            <p className="text-lg text-[#4A4A4A] font-body leading-relaxed max-w-xl mb-10 fade-in-up" style={{ animationDelay: "200ms" }}>
              Découvrez les voix françaises et francophones d'aujourd'hui. Romans,
              poésie, jeunesse, contes et histoire — chaque ouvrage est choisi avec
              passion pour vous offrir une expérience de lecture unique.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 fade-in-up" style={{ animationDelay: "300ms" }}>
              <Link
                to="/livres"
                data-testid="hero-explore-button"
                className="inline-flex items-center justify-center gap-2 bg-[#2C402E] text-[#FAF9F6] hover:bg-[#1F2D21] px-8 py-4 font-body tracking-wide transition-all group"
              >
                Explorer le catalogue
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/soumission"
                data-testid="hero-submit-button"
                className="inline-flex items-center justify-center gap-2 border border-[#2C402E] text-[#2C402E] hover:bg-[#2C402E] hover:text-[#FAF9F6] px-8 py-4 font-body transition-all"
              >
                Soumettre un manuscrit
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 fade-in-up" style={{ animationDelay: "400ms" }}>
            <div className="relative">
              <HeroStack />
              <div className="absolute -bottom-6 -left-6 bg-[#FAF9F6] border border-[#D1CFC7] px-6 py-4 hidden md:block z-50 shadow-lg">
                <p className="text-xs tracking-[0.2em] uppercase text-[#9E4735] font-body">Catalogue</p>
                <p className="font-serif text-3xl text-[#1A1A1A]">+ 20 titres</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee items={["Roman", "Poésie", "Histoire", "Jeunesse", "Contes", "Général"]} />

      {/* Livre vedette : Noces de paix à Kanou */}
      <FeaturedBook />

      <HomeStories />


      {/* Featured Books */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24" data-testid="featured-books-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#9E4735] font-body mb-4">
              Notre sélection
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-[#1A1A1A] leading-tight">
              Livres en vedette
            </h2>
          </div>
          <Link
            to="/livres"
            data-testid="view-all-books-link"
            className="inline-flex items-center gap-2 text-[#2C402E] font-body link-underline"
          >
            Voir tout le catalogue
            <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {featured.slice(0, 4).map((book, i) => (
            <BookCard key={book.id} book={book} index={i} onClick={() => setSelected(book)} />
          ))}
        </div>
      </section>

      {/* About section */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F4F1EA]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-[#9E4735] font-body mb-4">
              Notre Maison
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-[#1A1A1A] leading-tight mb-8">
              Une passion <em className="text-[#2C402E]">éditoriale</em>
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-lg text-[#4A4A4A] font-body leading-relaxed mb-6">
              Fondée à Paris, Challenges Littéraires Éditions (CLED) est une maison
              indépendante qui défend la diversité de la littérature francophone. Nous
              publions des auteurs émergents et confirmés, choisis pour la singularité
              de leur voix.
            </p>
            <p className="text-base text-[#757575] font-body leading-relaxed mb-8">
              Chaque livre est une rencontre. Nous accompagnons nos auteurs avec exigence
              et bienveillance, du manuscrit jusqu'à la librairie. Disponibles sur Amazon,
              dans toutes les librairies en Europe et chez nos partenaires en Afrique.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-12">
              <div>
                <p className="font-serif text-4xl text-[#2C402E]">+20</p>
                <p className="text-xs tracking-[0.2em] uppercase text-[#757575] font-body mt-1">Livres au catalogue</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-[#2C402E]">+20</p>
                <p className="text-xs tracking-[0.2em] uppercase text-[#757575] font-body mt-1">Auteurs publiés</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-[#2C402E]">2</p>
                <p className="text-xs tracking-[0.2em] uppercase text-[#757575] font-body mt-1">Collections éditoriales</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collections */}
      <Collections />


      {/* Mini section Lectures recommandées (apparaît si au moins 1 reco) */}
      {recommendations.length > 0 && (
        <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-[#F4F1EA]" data-testid="home-reco-section">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <BookHeart size={16} className="text-[#9E4735]" />
                <p className="text-xs tracking-[0.3em] uppercase text-[#9E4735] font-body">
                  Sélection partenaire · 7switch
                </p>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-[#1A1A1A] leading-tight">
                Lectures <em className="text-[#2C402E]">recommandées</em>
              </h2>
              <p className="text-sm text-[#757575] font-body mt-3 max-w-xl">
                Des coups de cœur disponibles chez notre librairie partenaire.
              </p>
            </div>
            <Link
              to="/lectures-recommandees"
              data-testid="home-reco-view-all"
              className="inline-flex items-center gap-2 text-[#2C402E] font-body link-underline"
            >
              Voir toute la sélection
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {recommendations.map((r, i) => (
              <article
                key={r.id}
                data-testid={`home-reco-card-${r.id}`}
                className="group flex flex-col bg-[#FAF9F6] border border-[#D1CFC7] hover:border-[#2C402E] transition-all fade-in-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="bg-[#F4F1EA] aspect-[3/4] flex items-center justify-center p-6 overflow-hidden">
                  <img
                    src={r.cover_url}
                    alt={r.title}
                    loading="lazy"
                    className="w-full h-full object-contain shadow-xl transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[10px] tracking-[0.25em] uppercase font-body text-[#9E4735] mb-2">
                    {recoGenreLabel(r.genre)}
                  </p>
                  <h3 className="font-serif text-xl text-[#1A1A1A] leading-tight font-light mb-1">
                    {r.title}
                  </h3>
                  <p className="text-sm font-body text-[#4A4A4A] mb-3">
                    par <em>{r.author}</em>
                  </p>
                  {r.price && (
                    <p className="font-mono text-base text-[#1A1A1A] mb-4">{r.price.toFixed(2)} €</p>
                  )}
                  <a
                    href={r.affiliate_url}
                    target="_blank"
                    rel="noreferrer sponsored"
                    data-testid={`home-reco-buy-${r.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 bg-[#C75D2C] text-[#FAF9F6] hover:bg-[#A84A20] px-5 py-2.5 text-sm font-body tracking-wide transition-all"
                  >
                    <ShoppingBag size={14} />
                    <span>Acheter sur</span>
                    <img src="/7switch-logo-white.png" alt="7switch" className="h-3.5" />
                    <ExternalLink size={12} />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}


      {/* News */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24" data-testid="news-section">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles size={16} className="text-[#9E4735]" />
          <p className="text-xs tracking-[0.3em] uppercase text-[#9E4735] font-body">
            Actualités
          </p>
        </div>
        <h2 className="font-serif text-4xl md:text-5xl font-light text-[#1A1A1A] leading-tight mb-16">
          Dernières nouvelles
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {news.map((item, i) => (
            <article
              key={item.id}
              data-testid={`news-card-${item.id}`}
              onClick={() => setSelectedNews(item)}
              className="fade-in-up cursor-pointer group"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              {item.image_url && (
                <div className="aspect-[4/3] overflow-hidden mb-5 bg-[#F4F1EA] relative">
                  <img src={item.image_url} alt={item.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  {item.gallery && item.gallery.length > 1 && (
                    <span className="absolute bottom-3 right-3 bg-[#1A1A1A]/80 text-[#FAF9F6] text-xs font-body px-2.5 py-1 flex items-center gap-1.5">
                      <Camera size={12} /> {item.gallery.length} photos
                    </span>
                  )}
                </div>
              )}
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735] font-body mb-2">
                {item.category} · {new Date(item.date).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
              </p>
              <h3 className="font-serif text-2xl text-[#1A1A1A] leading-tight mb-3 group-hover:text-[#2C402E] transition-colors">
                {item.title}
              </h3>
              <p className="text-sm text-[#4A4A4A] font-body leading-relaxed line-clamp-3">
                {item.excerpt}
              </p>
            </article>
          ))}
        </div>
      </section>
      <BookDetailDialog book={selected} open={!!selected} onClose={() => setSelected(null)} />
      <NewsDetailDialog item={selectedNews} open={!!selectedNews} onClose={() => setSelectedNews(null)} />
    </div>
  );
}
